import {
    StyleSheet,
    Text,
    View,
    FlatList,
    TouchableOpacity,
    Image,
    SafeAreaView,
    ActivityIndicator
} from 'react-native';
import React, { useEffect, useState } from 'react';
import BasicHeader from '../../components/BasicHeader';
import { useTranslation } from 'react-i18next';
import { API, COLORS, FONTS } from '../../../utils';
import { useNavigation } from '@react-navigation/native';
import useData from '../../hooks/useData';
import ProductRequestModal from '../../components/ProductRequestModal';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

// Reusable Product Card Component
const ProductCard = ({ item, onPress }) => {
    const getStatusColor = (status) => {
        switch (status) {
            case 'Approved': return '#4CAF50';
            case 'Rejected': return '#F44336';
            default: return '#FF9800';
        }
    };

    return (
        <TouchableOpacity
            style={styles.productCard}
            onPress={onPress}
            activeOpacity={0.7}
        >
            <Image source={{ uri: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSNAkQa9nHgRUWiCDunA1ARJaPBogJeI0GgLw&s'}} style={styles.productImage} />

            <View style={styles.productInfo}>
                <Text style={styles.productName}>{item.productName}</Text>
                <Text style={styles.manufacturer}>{item.manufacturer}</Text>
                <Text style={styles.description} numberOfLines={2}>
                    {item.description}
                </Text>

                <Text style={styles.date}>{item.date || item.created_at}</Text>
            </View>
        </TouchableOpacity>
    );
};

// Empty State Component
const EmptyProducts = () => (
    <View style={styles.emptyContainer}>
        <Text style={styles.emptyText}>No product requests found</Text>
        <Text style={styles.emptySubText}>Start by adding your first product request</Text>
    </View>
);

// Loading Component
const LoadingIndicator = () => (
    <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={COLORS.primary} />
        <Text style={styles.loadingText}>Loading your products...</Text>
    </View>
);

// FAB Component
const FabButton = ({ onPress }) => (
    <TouchableOpacity
        style={styles.fab}
        onPress={onPress}
        activeOpacity={0.8}
    >
        <Text style={styles.fabIcon}>+</Text>
    </TouchableOpacity>
);

export default function ProductRequest() {
    const { t } = useTranslation();
    const navigation = useNavigation();
    const { selectedProduct, setSelectedproduct } = useData();
    const [openProductModal, setOpenProductModal] = useState(false);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const insets = useSafeAreaInsets();

    // Fetch products from API
    const fetchProducts = async (isRefresh = false) => {
        if (isRefresh) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }

        console.log("Fetching products...");
        try {
            const token = await AsyncStorage.getItem("key");
            const userId = await AsyncStorage.getItem("id");

            if (!userId) {
                setLoading(false);
                setRefreshing(false);
                return;
            }

            const response = await fetch(`${API.baseurl}${API.getProductRequests}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const result = await response.json();

            const filteredData = result.data.filter(item => item.user_id === userId);

            setProducts(filteredData);
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    const handleProductPress = (item) => {
        setSelectedproduct(item);
        navigation.navigate("productChat", { productId: item.id });
    };

    const closeBottomSheet = () => {
        fetchProducts()
        setOpenProductModal(false);
    }

    const handleFabPress = () => setOpenProductModal(true);

    const handleRefresh = () => {
        fetchProducts(true);
    };

    const renderProductItem = ({ item }) => (
        <ProductCard item={item} onPress={() => handleProductPress(item)} />
    );

    // Show loading indicator on initial load
    if (loading) {
        return (
            <View style={[styles.container, { paddingTop: 0 }]}>
                <BasicHeader title={t("My Products")} />
                <LoadingIndicator />
            </View>
        );
    }

    return (
        <View style={[styles.container, { paddingTop: 0 }]}>
            <BasicHeader 
                title={t("My Products")} 
                onRefresh={handleRefresh}
                refreshing={refreshing}
            />

            {openProductModal &&
                <ProductRequestModal isVisible={openProductModal} onClose={closeBottomSheet} callbackFunction={fetchProducts} />
            }

            <View style={styles.mainContainer}>
                <FlatList
                    data={products}
                    renderItem={renderProductItem}
                    keyExtractor={(item) => item.id.toString()}
                    showsVerticalScrollIndicator={false}
                    contentContainerStyle={[
                        styles.listContainer,
                        { paddingBottom: insets.bottom + 80 }
                    ]}
                    ListEmptyComponent={<EmptyProducts />}
                    refreshing={refreshing}
                    onRefresh={handleRefresh}
                />
            </View>

            <View style={[
                styles.fabContainer,
                { bottom: insets.bottom + 20 }
            ]}>
                <FabButton onPress={handleFabPress} />
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: { 
        flex: 1, 
        backgroundColor: COLORS.background 
    },
    mainContainer: { 
        flex: 1 
    },
    fabContainer: { 
        position: 'absolute', 
        right: 20, 
        zIndex: 1000 
    },
    listContainer: { 
        padding: 16 
    },
    productCard: {
        flexDirection: 'row',
        backgroundColor: COLORS.white,
        borderRadius: 12,
        padding: 12,
        marginBottom: 12,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
    },
    productImage: { 
        width: 70, 
        height: 70, 
        borderRadius: 8, 
        marginRight: 12 
    },
    productInfo: { 
        flex: 1, 
        justifyContent: 'space-between' 
    },
    manufacturer: { 
        fontSize: 12, 
        fontFamily: FONTS.PoppinsMedium, 
        color: COLORS.gray, 
        textTransform: 'uppercase' 
    },
    productName: { 
        fontSize: 16, 
        fontFamily: FONTS.PoppinsSemiBold, 
        color: COLORS.dark, 
        marginTop: 2 
    },
    description: { 
        fontSize: 12, 
        fontFamily: FONTS.PoppinsRegular, 
        color: COLORS.gray, 
        marginTop: 4, 
        lineHeight: 16 
    },
    productMeta: { 
        flexDirection: 'row', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginTop: 8 
    },
    price: { 
        fontSize: 16, 
        fontFamily: FONTS.PoppinsBold, 
        color: COLORS.primary 
    },
    statusBadge: { 
        paddingHorizontal: 8, 
        paddingVertical: 4, 
        borderRadius: 6 
    },
    statusText: { 
        fontSize: 10, 
        fontFamily: FONTS.PoppinsMedium, 
        color: COLORS.white, 
        textTransform: 'uppercase' 
    },
    date: { 
        fontSize: 10, 
        fontFamily: FONTS.PoppinsRegular, 
        color: COLORS.lightGray, 
        marginTop: 4 
    },
    emptyContainer: { 
        alignItems: 'center', 
        justifyContent: 'center', 
        padding: 40 
    },
    emptyText: { 
        fontSize: 18, 
        fontFamily: FONTS.PoppinsSemiBold, 
        color: COLORS.gray, 
        textAlign: 'center' 
    },
    emptySubText: { 
        fontSize: 14, 
        fontFamily: FONTS.PoppinsRegular, 
        color: COLORS.lightGray, 
        textAlign: 'center', 
        marginTop: 8 
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    loadingText: {
        marginTop: 12,
        fontSize: 16,
        fontFamily: FONTS.PoppinsRegular,
        color: COLORS.gray,
    },
    fab: { 
        width: 60, 
        height: 60, 
        borderRadius: 30, 
        backgroundColor: COLORS.primary, 
        justifyContent: 'center', 
        alignItems: 'center', 
        shadowColor: '#000', 
        shadowOffset: { width: 0, height: 2 }, 
        shadowOpacity: 0.3, 
        shadowRadius: 4, 
        elevation: 6 
    },
    fabIcon: { 
        fontSize: 30, 
        color: COLORS.white, 
        fontWeight: 'bold' 
    },
});